
import Vue from "vue";
import CreateAnimationViewModel from "@models/animations/createAnimationViewModel";
import { Component, Watch } from "vue-property-decorator";
import { BACKGROUND_IMAGE_RATIO } from "@/appSettings";
import BackgroundCategoryListItem from "@/api/dataSource/api/Spirit/Admin/Interfaces/Models/BackgroundCategoryService/BackgroundCategoryListItem";
import CategoryViewModel from "@models/categoryViewModel";
import FilterRequest from "@/api/dataSource/api/Spirit/Common/Extensions/Models/FilterRequest";
import PaginationRequest from "@/api/dataSource/api/Common/RequestModels/PaginationRequest";
import SortRequest from "@/api/dataSource/api/Spirit/Common/Extensions/Models/SortRequest";
import { readPaginationCount, readPaginationItems } from "@/utils/pagination";
import BackgroundFilesGetListResponse from "@/api/dataSource/api/Spirit/Admin/Interfaces/Models/BackgroundFilesService/BackgroundFilesGetListResponse";
import MoveBackgroundsToCategoryRequest from "@/api/dataSource/api/Spirit/Admin/Interfaces/Models/BackgroundFilesService/MoveBackgroundsToCategoryRequest";

@Component
export default class PageCreateAnimationComponent extends Vue {
  isLoading = false;
  languages: string[] = [];
  categories: CategoryViewModel[] = [];
  backgroundImageRatio = BACKGROUND_IMAGE_RATIO;
  get title(): string {
    return this.id ? "Редактирование фона" : "Добавление фона";
  }
  get id(): string {
    const routeId = this.$route.params.id;
    return routeId ? routeId : "";
  }
  get categoryOptions(): CategoryViewModel[] {
    return [
      { id: null, name: "Без категории" } as CategoryViewModel,
      ...this.categories
    ];
  }
  vm: CreateAnimationViewModel = new CreateAnimationViewModel();
  async created() {
    this.languages = await this.$cacheProvider.getLanguagesAsync();
    this.vm.initLanguages(this.languages);
    await this.fetchCategories();
    if (this.$route.params.id) {
      this.applyRouteCategory();
      await this.fetchData();
    }
  }
  async fetchCategories() {
    const response = await this.$api.BackgroundCategoryService.getListAsync();
    this.categories = ((response && response.Items) || []).map(
      (item: BackgroundCategoryListItem) => new CategoryViewModel(item as any)
    );
  }
  @Watch("vm.fontColor")
  onFontColorChanged(value: string) {
    const normalizedValue = this.normalizeFontColor(value);
    if (normalizedValue !== value) {
      this.vm.fontColor = normalizedValue;
    }
  }
  applyRouteCategory() {
    const categoryId = this.$route.query.categoryId;
    if (Array.isArray(categoryId)) {
      return;
    }
    const parsedCategoryId = this.normalizeCategoryId(categoryId);
    if (parsedCategoryId != null) {
      this.vm.categoryId = parsedCategoryId;
    }
  }
  async fetchData() {
    this.isLoading = true;
    try {
      const current = await this.$api.BackgroundFileService.getDetailAsync(this.id);
      if (!current || !current.Id) {
        this.$notification.error("Фон не найден");
        this.$router.back();
        return;
      }
      this.vm.fromServerModel(current);
      if (this.vm.categoryId == null) {
        this.applyRouteCategory();
      }
      if (this.vm.categoryId == null || !this.vm.backgroundImage.ImageUrl) {
        await this.fetchCurrentCategory();
      }
    } catch (error) {
      this.$notification.error(error);
    } finally {
      this.isLoading = false;
    }
  }
  async fetchCurrentCategory() {
    const current = (await this.fetchBackgrounds()).find(
      (item) => this.getBackgroundId(item) === this.id
    );
    const categoryId = current ? this.getBackgroundCategoryId(current) : null;
    this.vm.categoryId = this.normalizeCategoryId(categoryId) == null
      ? this.vm.categoryId
      : this.normalizeCategoryId(categoryId);
    if (current && !this.vm.backgroundImage.ImageUrl) {
      this.vm.backgroundImage.ImageUrl = current.BackImageUrl || current.Url || current.AnimationUrl;
    }
  }
  async save() {
    const isValid = (this.$refs.validator as any).isValid();
    if (!isValid) return;
    this.isLoading = true;
    try {
      if (!this.vm.hasBackgroundImage) {
        this.$notification.error("Добавьте фоновое изображение");
        return;
      }
      if (!this.isFontColorValid()) {
        this.$notification.error("Цвет текста нужно указать в формате HEX без #, например FFFFFF");
        return;
      }
      this.vm.fontColor = this.normalizeFontColor(this.vm.fontColor);
      const categoryId = this.normalizeCategoryId(this.vm.categoryId);
      this.vm.categoryId = categoryId;
      const existingIds = !this.vm.id && categoryId != null
        ? await this.fetchBackgroundIds()
        : [];
      const request = this.vm.toServerModel();
      const res = await this.$api.BackgroundFileService.addAsync(request);
      if (res) {
        const backgroundId = this.vm.id || await this.findCreatedBackgroundId(existingIds);
        if (!backgroundId && categoryId != null) {
          throw new Error("Фон сохранен, но не удалось определить его идентификатор для привязки категории");
        }
        if (backgroundId && (this.vm.id || categoryId != null)) {
          await this.saveCategory(backgroundId, categoryId);
        }
        this.$notification.success(
          this.id ? "Фон успешно обновлен" : "Фон успешно добавлен"
        );
        this.$router.back();
      }
    } catch (error) {
      this.$notification.error(error);
    } finally {
      this.isLoading = false;
    }
  }
  async saveCategory(backgroundId: string, categoryId: number): Promise<void> {
    const normalizedCategoryId = this.normalizeCategoryId(categoryId);
    const request = new MoveBackgroundsToCategoryRequest({
      BackgroundFileIds: [backgroundId],
      CategoryId: normalizedCategoryId,
    });
    const categorySaved = await this.$api.BackgroundFileService.moveToCategoryAsync(
      request
    );
    if (!categorySaved) {
      throw new Error("Фон сохранен, но сервер не привязал категорию");
    }
    await this.ensureCategorySaved(backgroundId, normalizedCategoryId, request);
  }
  async fetchBackgrounds(categoryId: number = null): Promise<BackgroundFilesGetListResponse[]> {
    const normalizedCategoryId = this.normalizeCategoryId(categoryId);
    const pageSize = 100;
    const items: BackgroundFilesGetListResponse[] = [];
    let skip = 0;
    let total = 0;
    let pageItems: BackgroundFilesGetListResponse[] = [];

    do {
      const response = await this.$api.BackgroundFileService.getListAsync(
        ({
          Pagination: new PaginationRequest({
            Skip: skip,
            Take: pageSize,
          }),
          Sort: new SortRequest(),
        } as any)
      );
      pageItems = readPaginationItems<BackgroundFilesGetListResponse>(response);
      items.push(...pageItems);
      total = readPaginationCount(response) || items.length;
      skip += pageSize;
    } while (items.length < total && pageItems.length > 0);

    return normalizedCategoryId == null
      ? items
      : items.filter((item) => this.getBackgroundCategoryId(item) === normalizedCategoryId);
  }
  async fetchBackgroundIds(): Promise<string[]> {
    return (await this.fetchBackgrounds())
      .map((item) => this.getBackgroundId(item))
      .filter((id) => !!id);
  }
  async findCreatedBackgroundId(existingIds: string[]): Promise<string> {
    const existingIdMap = new Set(existingIds || []);
    const createdItems = (await this.fetchBackgrounds())
      .filter((item) => {
        const id = this.getBackgroundId(item);
        return item && id && !existingIdMap.has(id);
      });
    const created = createdItems.find((item) => this.hasSameTranslations(item)) || createdItems[0];
    return created ? this.getBackgroundId(created) : "";
  }
  getBackgroundId(item: any): string {
    return item ? item.Id || item.id || "" : "";
  }
  async ensureCategorySaved(
    backgroundId: string,
    categoryId: number,
    request: MoveBackgroundsToCategoryRequest
  ): Promise<void> {
    const expectedCategoryId = this.normalizeCategoryId(categoryId);
    const current = (await this.fetchBackgrounds()).find(
      (item) => this.getBackgroundId(item) === backgroundId
    );
    const savedCategoryId = current ? this.getBackgroundCategoryId(current) : null;

    if (current && savedCategoryId === expectedCategoryId) {
      return;
    }

    console.error("Background category move was accepted but not reflected by server read API", {
      moveRequest: request,
      backgroundId,
      expectedCategoryId,
      savedCategoryId,
      listItem: current,
    });
    throw new Error(
      `Сервер принял привязку категории, но не возвращает ее при чтении фона. Ожидалось: ${expectedCategoryId == null ? "null" : expectedCategoryId}, сервер вернул: ${savedCategoryId == null ? "null" : savedCategoryId}`
    );
  }
  getBackgroundCategoryId(item: any): number {
    const categoryId = item ? item.CategoryId ?? item.categoryId : null;
    return this.normalizeCategoryId(categoryId);
  }
  normalizeCategoryId(value: any): number {
    const categoryId = Number(value);
    return Number.isFinite(categoryId) && categoryId > 0 ? categoryId : null;
  }
  normalizeFontColor(value: any): string {
    return String(value || "").replace(/#/g, "").replace(/[^0-9a-fA-F]/g, "").slice(0, 6).toUpperCase();
  }
  isFontColorValid(): boolean {
    const fontColor = this.normalizeFontColor(this.vm.fontColor);
    return !fontColor || /^[0-9a-fA-F]{6}$/.test(fontColor);
  }
  hasSameTranslations(item: BackgroundFilesGetListResponse): boolean {
    const savedTranslations = item.Translations || [];
    return this.vm.translations.some((translation) => {
      const name = (translation.name || "").trim();
      if (!name) {
        return false;
      }
      return savedTranslations.some((savedTranslation) =>
        savedTranslation.LangCode === translation.langCode &&
        (savedTranslation.Name || "").trim() === name
      );
    });
  }
}
